import { DeviceHealthCountDTO } from '@activia/cm-api';
import { CountCardValueSize, IPieDataItem, themeTypeToCssVar } from '@activia/dataviz';
import {
  AnalyticsService,
  IModalConfig,
  IStandardDialogData,
  LayoutDeviceType,
  LayoutService,
  ModalDialogType,
  ModalService,
  RibbonComponent,
  ThemeType,
  WidgetComponent,
} from '@activia/ngx-components';
import { AuthFacade, PERMISSIONS } from '@amp/auth';
import { DevicesStatusService, HealthStatusCode, HealthStatusName, HealthStatusThemes } from '@amp/devices';
import { GlobalFacade, INewFeaturePreferences, NewFeatureData, NewFeatureService } from '@amp/global';
import { DurationPipe } from '@amp/ui';
import { formatNumber } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { DashboardWidgetsDrawerComponent } from './components/dashboard-widgets-drawer.component';
import { DashboardWidgetEnum, DashboardWidgetsPermissions } from './dashboard-widgets';
import { ServerOverviewService } from './server/server-overview.service';
import { DashboardFacade } from './store/dashboard.facade';

@Component({ selector: 'amp-dashboard', templateUrl: './dashboard.component.html', styleUrls: ['./dashboard.component.scss'] })
export class DashboardComponent implements OnDestroy, AfterViewInit {
  /**
   * device group name
   */
  deviceGroupName$: Observable<string>;
  /**
   * device group ID
   */
  deviceGroupId: number;

  /**
   * Specifies the total number of devices for the device health widget
   *
   * @ignore
   */
  deviceCount: number;

  /**
   * Specifies if the dashboard is pending
   *
   * @ignore
   */
  isDashboardPending$ = this.dashboardFacade.pending$;

  /**
   * Specifies if the dashboard is initiated
   *
   * @ignore
   */
  isDashboardInitiated$ = this.dashboardFacade.initiated$;

  /**
   * Specifies the visibility of each widget
   *
   * @ignore
   */
  widgetVisible = new Map<DashboardWidgetEnum, boolean>();

  /**
   * Specifies the permissions of each widget
   *
   * @ignore
   */
  widgetsPermissions = DashboardWidgetsPermissions;

  /**
   * The list of widgets
   *
   * @ignore
   */
  Widgets = DashboardWidgetEnum;

  /**
   * The list of widgets components
   *
   * @ignore
   */
  @ViewChildren(WidgetComponent) widgetComponents: QueryList<WidgetComponent>;

  /**
   * The dashboard ribbon
   *
   * @ignore
   */
  @ViewChild(RibbonComponent, { static: false }) ribbonComponent: RibbonComponent;

  /**
   * Pattern used to close all subscriptions
   *
   * @ignore
   */
  private componentDestroyed$: Subject<void> = new Subject();

  THEME_TYPES = ThemeType;
  preferences: INewFeaturePreferences;

  /**
   * Unseen new feature count to be displayed on ribbon icon
   */
  remainingNewFeatureCount: number;

  gridElementHeight$: Observable<number> = new Subject();

  gridColumns = {
    gigantic: 12,
    yuge: 12,
    huge: 12,
    web: 6,
    tablet: 4,
    handset: 1,
  };

  /**
   * here be the grid presets of the widgets
   * todo move to the widgets themselves
   */
  columnPresets = {
    numberCards: {
      gigantic: 4,
      yuge: 4,
      huge: 4,
      web: 2,
      tablet: 4,
      handset: 2,
    },
    deviceHealth: {
      gigantic: 4,
      yuge: 4,
      huge: 4,
      web: 2,
      tablet: 4,
      handset: 2,
    },
    timeSeries: {
      gigantic: 6,
      yuge: 6,
      huge: 6,
      web: 3,
      tablet: 4,
      handset: 2,
    },
    serverOverview: {
      gigantic: 4,
      yuge: 4,
      huge: 4,
      web: 2,
      tablet: 4,
      handset: 2,
    },
    contentStats: {
      gigantic: 6,
      yuge: 6,
      huge: 6,
      web: 3,
      tablet: 4,
      handset: 2,
    },
    processDetails: {
      gigantic: 12,
      yuge: 12,
      huge: 12,
      web: 6,
      tablet: 4,
      handset: 2,
    },
  };

  healthStatusDataSource: Array<IPieDataItem>;
  errorDevicesDataSource: { name: string; value: number };
  warningDevicesDataSource: { name: string; value: number };
  successDevicesDataSource: { name: string; value: number };
  unreachableDevicesDataSource: { name: string; value: number };
  notMonitoredDevicesDataSource: { name: string; value: number };
  cpuUptime$: Observable<number>;
  cpuCoreCount$: Observable<number>;
  cpuUsageThresholdTheme$: Observable<Array<{ ceilingValue: number; color: ThemeType }>>;
  chartCustomTheme = [ThemeType.SUCCESS, ThemeType.DANGER, ThemeType.WARNING, ThemeType.SECONDARY, ThemeType.INFO];
  healthStatusCodes = ['Ok', 'Warning', 'Error', 'Unreachable', 'Not Monitored'];

  thresholdTheme = [
    {
      ceilingValue: undefined,
      color: ThemeType.SUCCESS,
    },
    {
      ceilingValue: 70,
      color: ThemeType.WARNING,
    },
    {
      ceilingValue: 90,
      color: ThemeType.DANGER,
    },
  ];

  bandwidthUsageData = undefined;
  memoryUsageData = undefined;
  diskUsageData = undefined;
  systemLoadData = undefined;
  widgetDialogConfig: IModalConfig<IStandardDialogData> = undefined;

  HealthStatusCode = HealthStatusCode;
  HealthStatusThemes = HealthStatusThemes;
  CountCardValueSize = CountCardValueSize;

  systemLoadYAxisLabels: any = {
    '=0': '',
    '=1': 'dashboardScope.dashboard.system-load.cores.singular_50',
    other: 'dashboardScope.dashboard.system-load.cores.plural_50',
  };

  /** Indicates if no widgets are selected **/
  noWidgetsSelected$: Observable<boolean>;

  /**
   * @ignore
   */
  constructor(
    private deviceStatusService: DevicesStatusService,
    private overviewService: ServerOverviewService,
    private globalFacade: GlobalFacade,
    private dashboardFacade: DashboardFacade,
    public translateService: TranslocoService,
    private analyticsService: AnalyticsService,
    private newFeatureService: NewFeatureService,
    private router: Router,
    private durationPipe: DurationPipe,
    private modalService: ModalService,
    private authFacade: AuthFacade,
    private layoutService: LayoutService
  ) {
    this.deviceGroupName$ = this.globalFacade.defaultDeviceGroup$.pipe(
      map((defaultDeviceGroup) => {
        this.deviceGroupId = defaultDeviceGroup.id;
        this.deviceStatusService.refresh();
        return defaultDeviceGroup.name;
      }),
      takeUntil(this.componentDestroyed$),
    );

    /**
     * workaround until dynamic dashboards.
     * we calc the row heights for larger displays to fill the space
     */
    this.gridElementHeight$ = this.layoutService.deviceInfo$.pipe(
      map((li) => {
        if (li.deviceType === LayoutDeviceType.GIGANTIC) {
          return (li.height - 100) / 6.2;
        } else if (li.deviceType === LayoutDeviceType.YUGE) {
          return (li.height - 100) / 4.4;
        } else {
          return 50;
        }
      })
    );

    this.authFacade.authStateReady$
      .pipe(
        take(1),
        switchMap(() => this.authFacade.authenticatedUserPermissions$.pipe(take(1))),
        filter((permissions: Array<string>) => permissions.some((item) => item === PERMISSIONS.GET_MONITORING_VIEW)),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.deviceStatusService.start();

        // subscribe to the device health widget data observable
        this.deviceStatusService
          .getDevicesStatus()
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe((status: DeviceHealthCountDTO) => {
            this.deviceCount = status.ok + status.warning + status.error + status.unreachable + status.notMonitored;
            this.resetDeviceHealthWidget(status);
          });
      });

    this.cpuUptime$ = this.overviewService.latestCpuUptime$;
    this.cpuCoreCount$ = this.overviewService.cpuCoreCount$;
    this.cpuUsageThresholdTheme$ = this.cpuCoreCount$.pipe(
      distinctUntilChanged(),
      map((coreCount) => {
        let thresholdTheme = this.thresholdTheme;
        if (!!coreCount && coreCount > 1) {
          thresholdTheme = [
            {
              ceilingValue: undefined,
              color: ThemeType.SUCCESS,
            },
            {
              ceilingValue: coreCount * 0.7,
              color: ThemeType.WARNING,
            },
            {
              ceilingValue: coreCount * 0.9,
              color: ThemeType.DANGER,
            },
          ];
        }
        return thresholdTheme;
      }),
      startWith(this.thresholdTheme)
    );

    // Init the dashboard, that will load the user preferences
    this.dashboardFacade.initDashboard();

    // set the visibility of the widgets
    this.dashboardFacade.widgets$.pipe(takeUntil(this.componentDestroyed$)).subscribe((widgets) => {
      // for each widget, check if its visible
      Object.keys(DashboardWidgetEnum).forEach((widget: DashboardWidgetEnum) => {
        // get store value or true if no settings exist (display all widgets by default)
        const visible = widgets.filter((w) => w.name === widget.toString()).map((w) => w.settings.visible)[0] !== false;

        this.widgetVisible.set(widget, visible);
      });
    });

    this.globalFacade.newFeaturePreferences$.pipe(takeUntil(this.componentDestroyed$)).subscribe((preferences) => {
      this.preferences = preferences;
      this.remainingNewFeatureCount = NewFeatureData.length - 1 - preferences.pausedAtStepId;
    });

    this.bandwidthUsageData = [
      {
        label: this.translateService.translate('dashboardScope.dashboard.bw-usage.Downloads'),
        datumSerieSource: this.overviewService.latestNetworkReceive$,
      },
      {
        label: this.translateService.translate('dashboardScope.dashboard.bw-usage.Uploads'),
        datumSerieSource: this.overviewService.latestNetworkSend$,
      },
    ];
    this.systemLoadData = [
      {
        label: this.translateService.translate('dashboardScope.dashboard.system-load.title_30'),
        datumSerieSource: this.overviewService.latestCpuLoad$,
      },
    ];
    this.memoryUsageData = [
      {
        label: this.translateService.translate('dashboardScope.dashboard.mem-usage.Memory'),
        datumSerieSource: this.overviewService.latestMemoryUsage$,
      },
    ];
    this.diskUsageData = [
      {
        label: this.translateService.translate('dashboardScope.dashboard.disk-usage.Disk'),
        datumSerieSource: this.overviewService.latestDiskUsage$,
      },
    ];

    // open a modal to confirm the closure
    const dialogData: IStandardDialogData = {
      type: ModalDialogType.Confirm,
      theme: ThemeType.PRIMARY,
      title: this.translateService.translate('dashboardScope.dashboard.dialog.title-confirm-remove-widget'),
      message: this.translateService.translate('dashboardScope.dashboard.dialog.message-confirm-remove-widget'),
      closeActionLabel: this.translateService.translate('button.cancel'),
      acceptActionLabel: this.translateService.translate('button.remove'),
    };

    this.widgetDialogConfig = {
      showCloseIcon: true,
      closeOnBackdropClick: true,
      data: dialogData,
    };

    this.noWidgetsSelected$ = this.dashboardFacade.selectedWidgets$.pipe(map((selectedWidgets) => selectedWidgets.length === 0));
  }

  /**
   * Resets the device health widget labels and data
   *
   * @ignore
   */
  resetDeviceHealthWidget(status: DeviceHealthCountDTO): void {
    this.healthStatusDataSource = Object.keys(HealthStatusName).map((s, idx) => ({
        name: this.translateService.translate(`deviceFields.DEVICE.ENUM.HEALTH_STATUS.${s}`),
        value: status[HealthStatusName[s]],
        color: themeTypeToCssVar(HealthStatusThemes[idx]),
      }));
    this.errorDevicesDataSource = this.getDeviceHealthErrorDataSource(status);
    this.warningDevicesDataSource = this.getDeviceHealthWarningDataSource(status);
    this.successDevicesDataSource = this.getDeviceHealthSuccessDataSource(status);
    this.unreachableDevicesDataSource = this.getDeviceHealthUnreachableDataSource(status);
    this.notMonitoredDevicesDataSource = this.getDeviceHealthNotMonitoredDataSource(status);
  }

  /** Format the value of monitoring speed, cpu, memory or disk usage */
  formatValue(value: number): number {
    return Math.round(value * 100) / 100;
  }

  /** Format the value of bandwidth usage */
  formatBandwidthUsageValue(value: number): number {
    return Math.round((value / 1024) * 100) / 100;
  }

  /**
   * Returns the errorDeviceOverviewChart chart options
   *
   * @ignore
   */
  private getDeviceHealthErrorDataSource(status: DeviceHealthCountDTO): { name: string; value: number } {
    return {
      name: this.translateService.translate('dashboardScope.dashboard.devices-count-label', { value: formatNumber(status.error, this.translateService.getActiveLang()) }),
      value: this.deviceCount === 0 ? 0 : (status.error / this.deviceCount) * 100,
    };
  }

  /**
   * Returns the warningDeviceOverviewChart chart options
   *
   * @ignore
   */
  private getDeviceHealthWarningDataSource(status: DeviceHealthCountDTO): { name: string; value: number } {
    return {
      name: this.translateService.translate('dashboardScope.dashboard.devices-count-label', { value: formatNumber(status.warning, this.translateService.getActiveLang()) }),
      value: this.deviceCount === 0 ? 0 : (status.warning / this.deviceCount) * 100,
    };
  }

  /**
   * Returns the successDeviceOverviewChart chart options
   *
   * @ignore
   */
  private getDeviceHealthSuccessDataSource(status: DeviceHealthCountDTO): { name: string; value: number } {
    return {
      name: this.translateService.translate('dashboardScope.dashboard.devices-count-label', { value: formatNumber(status.ok, this.translateService.getActiveLang()) }),
      value: this.deviceCount === 0 ? 0 : (status.ok / this.deviceCount) * 100,
    };
  }

  /**
   * Returns the errorDeviceOverviewChart chart options
   *
   * @ignore
   */
  private getDeviceHealthUnreachableDataSource(status: DeviceHealthCountDTO): { name: string; value: number } {
    return {
      name: this.translateService.translate('dashboardScope.dashboard.devices-count-label', { value: formatNumber(status.unreachable, this.translateService.getActiveLang()) }),
      value: this.deviceCount === 0 ? 0 : (status.unreachable / this.deviceCount) * 100,
    };
  }

  /**
   * Returns the errorDeviceOverviewChart chart options
   *
   * @ignore
   */
  private getDeviceHealthNotMonitoredDataSource(status: DeviceHealthCountDTO): { name: string; value: number } {
    return {
      name: this.translateService.translate('dashboardScope.dashboard.devices-count-label', { value: formatNumber(status.notMonitored, this.translateService.getActiveLang()) }),
      value: this.deviceCount === 0 ? 0 : (status.notMonitored / this.deviceCount) * 100,
    };
  }

  /**
   * @ignore
   */
  public ngOnDestroy(): void {
    this.deviceStatusService.stop();
    this.overviewService.stop();
    // unsubscribe all subscriptions
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  /**
   * @ignore
   */
  ngAfterViewInit(): void {
    // persist the widgets settings when closing, lets subscribe to the widget close event
    // do that on the changes event because widgets can be added dynamically
    this.subscribeWidgetsOnClose(this.widgetComponents);

    this.widgetComponents.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe((widgetList: QueryList<WidgetComponent>) => {
      this.subscribeWidgetsOnClose(widgetList);
    });

    // attach an action to the ribbon fab button
    this.ribbonComponent.fabActionClicked.pipe(takeUntil(this.componentDestroyed$)).subscribe((_) =>
      this.modalService.openDrawer<DashboardWidgetsDrawerComponent, any>(DashboardWidgetsDrawerComponent, {
        data: {},
      })
    );
  }

  private subscribeWidgetsOnClose(widgetComponents: QueryList<WidgetComponent>) {
    widgetComponents.forEach((widget) => {
      // subscribe to the widget close event
      widget.close
        .pipe(
          takeUntil(this.widgetComponents.changes.pipe(take(1))),
          switchMap((_) => {
            const modalRef = this.modalService.openStandardDialog(this.widgetDialogConfig);
            // close the widget if the user confirmed
            return modalRef.componentInstance.accepted;
          }),
          takeUntil(this.componentDestroyed$),
        )
        .subscribe((_) => {
          // save the widgets settings, that will auto close the widget
          this.dashboardFacade.updateWidgetSettings(widget.name, false);

          // also send the analytics
          this.sendAnalytics(widget.name);
        });
    });
  }

  /**
   * Open new feature tour dialog
   */
  openNewFeatureTour() {
    this.newFeatureService.openNewFeatureTour(this.preferences, false);
  }

  navigateToDeviceStatus($event): void {
    this.router.navigate([
      'app',
      'legacy-module',
      {
        module: 'QuickSearchDevices',
        title: `Devices in ${$event} State`,
        methodToCall: 'quickSearch',
        requestId: 1,
        context: 'std_ctxt',
        qSearch: `Health Status=${this.healthStatusCodes.indexOf($event)}`,
        scope: this.deviceGroupId,
      },
    ]);
  }

  /**
   * Send analytics about the widgets being added/removed
   *
   * @ignore
   */
  private sendAnalytics(widgetName: string) {
    this.analyticsService.trackEvent({
      eventCategory: 'dashboard.widgets',
      eventAction: 'remove',
      eventLabel: widgetName,
    });
  }

  numberCardPercentFormat(data): any {
    return `${data.toFixed(+2)}`;
  }

  uptimeFormat = (data): any => this.durationPipe.transform(data * 1000);
}
